import {
  fetchAndActivate,
  getRemoteConfig,
  getValue,
  RemoteConfig,
} from 'firebase/remote-config';
import { getFirebaseApp } from './app';
import { isFirebaseConfigured } from './config';

export const MENU_MANAGEMENT_LIVE_CONFIG_WEB_KEY =
  'menu_management_live_config_web';
export const MENU_MANAGEMENT_SANDBOX_CONFIG_WEB_KEY =
  'menu_management_sandbox_config_web';

/** Production uses live web JSON; development uses sandbox web parameter. */
export function getMenuManagementRemoteConfigParamKey(): string {
  return process.env.REACT_APP_NODE_ENV === 'production'
    ? MENU_MANAGEMENT_LIVE_CONFIG_WEB_KEY
    : MENU_MANAGEMENT_SANDBOX_CONFIG_WEB_KEY;
}

let remoteConfigInstance: RemoteConfig | null = null;
let fetchPromise: Promise<boolean> | null = null;

function isNonProductionDeployment(): boolean {
  if (process.env.REACT_APP_NODE_ENV !== 'production') {
    return true;
  }

  if (typeof window === 'undefined') {
    return false;
  }

  const host = window.location.hostname.toLowerCase();
  return (
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host.startsWith('uat.') ||
    host.includes('.uat.')
  );
}

function getMinimumFetchIntervalMillis(): number {
  // Local `npm start` always refetches on refresh.
  if (process.env.NODE_ENV === 'development') {
    return 0;
  }

  // UAT / staging / sandbox builds should pick up Remote Config on each load.
  if (isNonProductionDeployment()) {
    return 0;
  }

  const override = process.env.REACT_APP_REMOTE_CONFIG_MIN_FETCH_MS;
  if (override !== undefined && override !== '') {
    const parsed = Number(override);
    if (!Number.isNaN(parsed) && parsed >= 0) {
      return parsed;
    }
  }

  return 3_600_000;
}

function getRc(): RemoteConfig | null {
  if (!isFirebaseConfigured()) {
    return null;
  }
  if (!remoteConfigInstance) {
    const rc = getRemoteConfig(getFirebaseApp());
    rc.settings.minimumFetchIntervalMillis = getMinimumFetchIntervalMillis();
    remoteConfigInstance = rc;
  }
  return remoteConfigInstance;
}

export async function fetchAndActivateRemoteConfig(
  options: { force?: boolean } = {},
): Promise<boolean> {
  const rc = getRc();
  if (!rc) {
    return false;
  }
  if (options.force) {
    fetchPromise = null;
  }
  if (!fetchPromise) {
    fetchPromise = fetchAndActivate(rc).finally(() => {
      fetchPromise = null;
    });
  }
  return fetchPromise;
}

/** String parameter (define the key in Firebase Console → Remote Config → Parameters). */
export function getRemoteConfigString(key: string, defaultValue = ''): string {
  const rc = getRc();
  if (!rc) {
    return defaultValue;
  }
  return getValue(rc, key).asString();
}

export function getRemoteConfigBoolean(
  key: string,
  defaultValue = false,
): boolean {
  const rc = getRc();
  if (!rc) {
    return defaultValue;
  }
  return getValue(rc, key).asBoolean();
}

export function getRemoteConfigNumber(key: string, defaultValue = 0): number {
  const rc = getRc();
  if (!rc) {
    return defaultValue;
  }
  return getValue(rc, key).asNumber();
}
